import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Mail, User, Building, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';

export const ContactSection = ({ t }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t.contact.form.validation.nameRequired;
    }

    if (!formData.email.trim()) {
      newErrors.email = t.contact.form.validation.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t.contact.form.validation.emailInvalid;
    }

    if (!formData.message.trim()) {
      newErrors.message = t.contact.form.validation.messageRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const subject = encodeURIComponent(
        formData.company
          ? `Contact from ${formData.name} (${formData.company})`
          : `Contact from ${formData.name}`
      );
      const body = encodeURIComponent(
        [
          `Name: ${formData.name}`,
          `Email: ${formData.email}`,
          formData.company ? `Company: ${formData.company}` : null,
          '',
          formData.message
        ]
          .filter(Boolean)
          .join('\n')
      );

      // Static landing: open the user's mail client (no backend needed)
      window.location.href = `mailto:info@valtellina-software.ch?subject=${subject}&body=${body}`;

      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18128204450/contact_form_submit'
        });
      }

      toast.success(t.contact.form.success);
      setFormData({ name: '', email: '', company: '', message: '' });
      setErrors({});
    } catch (error) {
      console.error('Error preparing email:', error);
      toast.error(t.contact.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            {t.contact.title}
          </h2>
          <p className="text-xl text-gray-300">
            {t.contact.subtitle}
          </p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-200 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {t.contact.form.name}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`bg-slate-900/50 border-slate-600 text-white focus:border-blue-500 ${
                      errors.name ? 'border-red-500' : ''
                    }`}
                    placeholder="John Doe"
                  />
                  {errors.name && (
                    <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-200 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {t.contact.form.email}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="text"
                    value={formData.email}
                    onChange={handleChange}
                    className={`bg-slate-900/50 border-slate-600 text-white focus:border-blue-500 ${
                      errors.email ? 'border-red-500' : ''
                    }`}
                    placeholder="john@example.com"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company" className="text-gray-200 flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  {t.contact.form.company}
                </Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="bg-slate-900/50 border-slate-600 text-white focus:border-blue-500"
                  placeholder="Company Ltd."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-gray-200 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  {t.contact.form.message}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className={`bg-slate-900/50 border-slate-600 text-white focus:border-blue-500 resize-none ${
                    errors.message ? 'border-red-500' : ''
                  }`}
                  placeholder={t.contact.form.message}
                />
                {errors.message && (
                  <p className="text-red-400 text-sm mt-1">{errors.message}</p>
                )}
              </div>

              <Button 
                type="submit" 
                size="lg" 
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-pulse">{t.contact.form.sending}</span>
                  </>
                ) : (
                  <>
                    {t.contact.form.submit}
                    <Send className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>

            {/* Contact Email Info */}
            <div className="mt-8 pt-6 border-t border-slate-700 text-center">
              <p className="text-gray-400 text-sm mb-2">Or reach us directly at:</p>
              <a 
                href="mailto:info@valtellina-software.ch"
                className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
              >
                info@valtellina-software.ch
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
